import { useMemo, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { cartoesApi, contasApi, pluggyPendenciasApi, pluggyVinculosApi } from '../../api/endpoints'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Modal } from '../../components/ui/Modal'
import { LoadingState, ErrorState } from '../../components/ui/EmptyState'
import { useToast } from '../../components/ui/Toast'
import { formatMoney } from '../../lib/utils'
import { diaMes, diaMesAno, hojeISO, lerValor, primeiroDoMes, primeiroDoMesPassado, useDebounced } from '../../lib/corte'
import { Divergencias, PreviaPluggy } from '../contas/PreviaSaldo'
import { AceitarTudoButton } from './AceitarTudo'
import { invalidarPluggy, soNovas } from './invalidar'
import type { ConfiguracaoVinculoInfo, ResumoConfiguracaoInfo } from '../../types'

const ATALHO = 'rounded-full border px-4 py-2 text-13 transition-colors'

function Etiqueta({ children }: { children: ReactNode }) {
  return <span className="ml-2 rounded-full bg-income/15 px-2 py-0.5 text-2xs font-medium text-income">{children}</span>
}

function diaDoMes(iso?: string | null): number | undefined {
  if (!iso) return undefined
  const n = Number(iso.slice(8, 10))
  return n >= 1 && n <= 31 ? n : undefined
}

/** Assistente de corte e saldo de um vínculo Pluggy (conta ou cartão), seguido do resumo da importação. */
export function AssistenteCorte({ vinculoId, onClose }: { vinculoId: string; onClose: () => void }) {
  const [resumo, setResumo] = useState<ResumoConfiguracaoInfo | null>(null)
  const cfg = useQuery({
    queryKey: ['pluggy-configuracao', vinculoId],
    queryFn: () => pluggyVinculosApi.configuracaoVinculo(vinculoId),
    staleTime: 0,
    gcTime: 0,
  })

  if (cfg.isLoading || cfg.isError || !cfg.data) {
    return (
      <Modal open onClose={onClose} title="Corte e saldo">
        {cfg.isError ? <ErrorState message="Não foi possível carregar a configuração desta conta." /> : <LoadingState />}
      </Modal>
    )
  }
  if (resumo) return <ResumoImportacao cfg={cfg.data} resumo={resumo} vinculoId={vinculoId} onClose={onClose} />
  return <FormularioCorte cfg={cfg.data} vinculoId={vinculoId} onClose={onClose} onConcluido={setResumo} />
}

function FormularioCorte({ cfg, vinculoId, onClose, onConcluido }: {
  cfg: ConfiguracaoVinculoInfo
  vinculoId: string
  onClose: () => void
  onConcluido: (r: ResumoConfiguracaoInfo) => void
}) {
  const qc = useQueryClient()
  const toast = useToast()
  const ehCartao = cfg.tipo === 'cartao'
  const sugestao = cfg.sugestaoCorte.slice(0, 10)
  const saldoInicial = cfg.saldoPluggy ?? cfg.saldoWalleon
  const saldoInicialTxt = saldoInicial != null ? saldoInicial.toFixed(2) : ''

  const [data, setData] = useState(sugestao)
  const [saldoTxt, setSaldoTxt] = useState(saldoInicialTxt)
  const [entendi, setEntendi] = useState(false)

  const { data: vinculos = [] } = useQuery({ queryKey: ['pluggy-vinculos'], queryFn: pluggyVinculosApi.list })
  const { data: cartoes = [] } = useQuery({ queryKey: ['cartoes'], queryFn: cartoesApi.list, enabled: ehCartao })
  const vinculo = vinculos.find((v) => v.id === vinculoId)
  const contaId = vinculo?.financialAccountId
  const cartaoAtual = cartoes.find((c) => c.id === vinculo?.cartaoId)

  // Cartão: valores do banco (etiqueta "do banco"), editáveis.
  const banco = {
    limite: cfg.cartao?.limite ?? cfg.creditData?.limite ?? undefined,
    fechamento: cfg.cartao?.diaFechamento ?? diaDoMes(cfg.creditData?.dataFechamento),
    vencimento: cfg.cartao?.diaVencimento ?? diaDoMes(cfg.creditData?.dataVencimento),
  }
  const [limite, setLimite] = useState<string | null>(null)
  const [fechamento, setFechamento] = useState<string | null>(null)
  const [vencimento, setVencimento] = useState<string | null>(null)
  const limiteTxt = limite ?? String(banco.limite ?? cartaoAtual?.limite ?? '')
  const fechamentoTxt = fechamento ?? String(banco.fechamento ?? cartaoAtual?.diaFechamento ?? '')
  const vencimentoTxt = vencimento ?? String(banco.vencimento ?? cartaoAtual?.diaVencimento ?? '')

  const saldo = lerValor(saldoTxt)
  const dataValida = /^\d{4}-\d{2}-\d{2}$/.test(data) && data <= hojeISO()
  const dataD = useDebounced(data)
  const saldoD = useDebounced(saldo)
  const dataDValida = /^\d{4}-\d{2}-\d{2}$/.test(dataD) && dataD <= hojeISO()

  // O que a importação vai trazer (inclui os lançamentos que a Pluggy ainda tem e o Walleon não): vem do servidor, que lê o banco
  // desde o corte e decide cada transação como a própria importação. Somente leitura.
  const previaQ = useQuery({
    queryKey: ['previa-pluggy', vinculoId, dataD, ehCartao ? null : saldoD],
    queryFn: ({ signal }) => pluggyVinculosApi.previaConfiguracao(vinculoId, { dataCorte: dataD, saldo: ehCartao ? undefined : saldoD ?? undefined }, signal),
    enabled: dataDValida && (ehCartao || saldoD != null),
    placeholderData: (anterior) => anterior,
    staleTime: 30_000,
    retry: false,
  })
  const previaPluggy = previaQ.data
  const calculando = previaQ.isFetching || data !== dataD || saldo !== saldoD

  // Conta que já tem lançamentos: o efeito sobre eles (histórico, divergências) vem da prévia do saldo de abertura.
  const efeitoQ = useQuery({
    queryKey: ['previa-corte', contaId, dataD, saldoD],
    queryFn: () => contasApi.previaSaldoAbertura(contaId!, { dataCorte: dataD, saldo: saldoD! }),
    enabled: !ehCartao && cfg.contaTemLancamentos && !!contaId && dataDValida && saldoD != null,
    placeholderData: (anterior) => anterior,
  })
  const previa = cfg.contaTemLancamentos ? (efeitoQ.data ?? cfg.efeito) : undefined

  const fechamentoExibido = useMemo(() => {
    const [y, m, d] = sugestao.split('-').map(Number)
    const f = new Date(y, m - 1, d - 1)
    return `${String(f.getDate()).padStart(2, '0')}/${String(f.getMonth() + 1).padStart(2, '0')}`
  }, [sugestao])

  const configurar = useMutation({
    mutationFn: async () => {
      if (ehCartao && vinculo?.cartaoId) {
        const patch: Record<string, number> = {}
        if (limite !== null && lerValor(limite) != null) patch.limite = lerValor(limite)!
        if (fechamento !== null && Number(fechamento) >= 1) patch.diaFechamento = Number(fechamento)
        if (vencimento !== null && Number(vencimento) >= 1) patch.diaVencimento = Number(vencimento)
        if (Object.keys(patch).length) await cartoesApi.atualizar(vinculo.cartaoId, patch)
      }
      return pluggyVinculosApi.configurarVinculo(vinculoId, {
        dataCorte: data,
        saldoAtual: ehCartao ? undefined : saldo,
        confirmarEfeito: ehCartao ? false : cfg.contaTemLancamentos && entendi,
      })
    },
    onSuccess: (r) => { invalidarPluggy(qc); onConcluido(r) },
    onError: (e: Error) => toast.error(e.message || 'Não foi possível importar. Tente de novo.'),
  })
  const padrao = useMutation({
    mutationFn: () => pluggyVinculosApi.configurarPadraoVinculo(vinculoId),
    onSuccess: (r) => { invalidarPluggy(qc); onConcluido(r) },
    onError: (e: Error) => toast.error(e.message || 'Não foi possível aplicar o padrão.'),
  })
  const ocupado = configurar.isPending || padrao.isPending

  const podeConfirmar = dataValida && !ocupado &&
    !calculando && (ehCartao || (saldo != null && (!cfg.contaTemLancamentos || (!!previa && entendi))))

  const textoPadrao = ehCartao
    ? `Pular usa o padrão: compras a partir do início da fatura aberta (${diaMesAno(sugestao)}).`
    : `Pular usa o padrão: corte no 1º do mês (${diaMes(primeiroDoMes())}) e o saldo que o banco informou${cfg.saldoPluggy != null ? ` (${formatMoney(cfg.saldoPluggy)})` : ''}.`

  const ativo = (iso: string) => data === iso ? 'border-primary bg-primary/15 text-primary font-semibold' : 'border-border bg-surface2 text-white'

  return (
    <Modal
      open
      onClose={ocupado ? () => {} : onClose}
      size="lg"
      title={`${ehCartao ? 'Corte do cartão' : 'Corte e saldo'} · ${cfg.nomeConta}`}
      footer={
        <>
          <Button variant="ghost" onClick={() => padrao.mutate()} loading={padrao.isPending} disabled={configurar.isPending}>Pular</Button>
          <Button data-tutorial="corte.confirmar" icon="ri-download-2-line" onClick={() => configurar.mutate()} loading={configurar.isPending} disabled={!podeConfirmar}>Confirmar e importar</Button>
        </>
      }
    >
      {ocupado && <LoadingState message="Importando lançamentos do banco... isso pode levar alguns instantes." />}
      <div className={ocupado ? 'hidden' : 'space-y-4'}>
        {ehCartao ? (
          <>
            <p className="text-13 text-muted">Dados lidos do banco. Tudo pode ser editado.</p>
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <Input label="Limite total (R$)" type="number" step="0.01" value={limiteTxt} onChange={(e) => setLimite(e.target.value)} />
                {banco.limite != null && limite === null && <Etiqueta>do banco</Etiqueta>}
              </div>
              <div>
                <Input label="Fecha no dia" type="number" min={1} max={31} value={fechamentoTxt} onChange={(e) => setFechamento(e.target.value)} />
                {banco.fechamento != null && fechamento === null && <Etiqueta>do banco</Etiqueta>}
              </div>
              <div>
                <Input label="Vence no dia" type="number" min={1} max={31} value={vencimentoTxt} onChange={(e) => setVencimento(e.target.value)} />
                {banco.vencimento != null && vencimento === null && <Etiqueta>do banco</Etiqueta>}
              </div>
            </div>
            <div className="space-y-2 rounded border border-info/50 bg-surface2/40 p-4">
              <p className="text-13 font-semibold text-info">Corte do cartão: início da fatura aberta</p>
              <div className="flex justify-between text-13"><span className="text-muted">Último fechamento</span><b className="text-white">{fechamentoExibido}</b></div>
              <Input data-tutorial="corte.data" label="Importar compras a partir de" type="date" max={hojeISO()} value={data} onChange={(e) => setData(e.target.value)} />
              {cfg.saldoPluggy != null && (
                <div className="flex justify-between text-13"><span className="text-muted">Fatura aberta hoje (banco)</span><b className="text-white">{formatMoney(Math.abs(cfg.saldoPluggy))}</b></div>
              )}
              <PreviaPluggy
                previa={previaPluggy} calculando={calculando} erro={previaQ.isError ? previaQ.error.message : null}
                dataCorte={data} saldoBanco={null} ehCartao
              />
              <p className="text-2xs text-muted">
                Assim a fatura aberta do Walleon bate com a do banco, sem lançamento de ajuste. Compra de cartão não mexe no saldo da conta, só no limite usado e na fatura. A data é editável.
              </p>
            </div>
          </>
        ) : (
          <>
            <p className="text-13 text-muted">
              A conta conectada traz o histórico do banco. Para o saldo ficar certo desde o primeiro dia, escolha a partir de quando você quer os lançamentos. O que for anterior fica de fora.
            </p>
            <div className="space-y-2">
              <Input data-tutorial="corte.data" label="Importar lançamentos a partir de" type="date" max={hojeISO()} value={data} onChange={(e) => setData(e.target.value)} />
              <div className="flex flex-wrap gap-2">
                <button type="button" className={`${ATALHO} ${ativo(primeiroDoMes())}`} onClick={() => setData(primeiroDoMes())}>1º do mês</button>
                <button type="button" className={`${ATALHO} ${ativo(primeiroDoMesPassado())}`} onClick={() => setData(primeiroDoMesPassado())}>1º do mês passado</button>
              </div>
              <p className="text-2xs text-muted">Mais para trás traz mais histórico, mas pode mexer em orçamentos de meses anteriores.</p>
              {data > hojeISO() && <p className="text-13 text-expense" role="alert">A data de corte não pode ser futura.</p>}
            </div>
            <div>
              <Input data-tutorial="corte.saldo" label="Saldo de hoje (o app já trouxe do banco)" type="number" step="0.01" value={saldoTxt} onChange={(e) => setSaldoTxt(e.target.value)} />
              {cfg.saldoPluggy != null && saldo === cfg.saldoPluggy && <Etiqueta>do banco</Etiqueta>}
            </div>
            <PreviaPluggy
              previa={previaPluggy} calculando={calculando} erro={previaQ.isError ? previaQ.error.message : null}
              dataCorte={data} saldoBanco={cfg.saldoPluggy} ehCartao={false}
            />
            {previa && <Divergencias lista={previa.divergencias} />}
            {cfg.contaTemLancamentos && (
              <div className="space-y-2 rounded border border-warning/50 bg-warning/10 p-3 text-13 text-warning">
                <p>
                  <i className="ri-error-warning-line" aria-hidden /> Esta conta já tem lançamentos
                  {previa ? `: ${previa.lancamentosHistorico} anteriores ao corte viram histórico e deixam de mexer no saldo` : ''}. O saldo será recalculado a partir do saldo inicial.
                </p>
                <label className="flex cursor-pointer items-center gap-2 text-white">
                  <input type="checkbox" checked={entendi} onChange={(e) => setEntendi(e.target.checked)} />
                  Entendi o efeito e quero continuar
                </label>
              </div>
            )}
          </>
        )}
        {[...cfg.avisos, ...(previaPluggy?.avisos ?? []).filter((a) => !cfg.avisos.includes(a))].map((a) => (
          <p key={a} className="text-13 text-warning"><i className="ri-information-line" aria-hidden /> {a}</p>
        ))}
        <p className="text-2xs text-muted">{textoPadrao}</p>
      </div>
    </Modal>
  )
}

function Linha({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-13">
      <span className="text-muted">{rotulo}</span>
      <b className="text-right text-white">{children}</b>
    </div>
  )
}

function ResumoImportacao({ cfg, resumo, vinculoId, onClose }: {
  cfg: ConfiguracaoVinculoInfo
  resumo: ResumoConfiguracaoInfo
  vinculoId: string
  onClose: () => void
}) {
  const { data: pendencias = [] } = useQuery({ queryKey: ['pluggy-pendencias'], queryFn: pluggyPendenciasApi.list })
  const doVinculo = pendencias.filter((p) => p.vinculoId === vinculoId)
  const curta = diaMes(resumo.dataCorte)
  const ehCartao = cfg.tipo === 'cartao'
  const mostraSaldo = !ehCartao && resumo.saldoAgora != null && resumo.saldoFinal != null
  const pct = mostraSaldo && resumo.saldoFinal !== 0
    ? Math.max(0, Math.min(100, (resumo.saldoAgora! / resumo.saldoFinal!) * 100))
    : 100

  function irParaCaixa() {
    onClose()
    setTimeout(() => document.getElementById('caixa-de-entrada')?.scrollIntoView({ behavior: 'smooth' }), 150)
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={ehCartao ? 'Cartão configurado' : 'Conta configurada'}
      footer={
        <>
          <Button variant="light" onClick={onClose}>Fechar</Button>
          {soNovas(doVinculo).length > 0 && <AceitarTudoButton pendencias={doVinculo} />}
          {resumo.pendencias > 0 && <Button icon="ri-inbox-line" onClick={irParaCaixa}>Ir para a caixa de entrada</Button>}
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-13 text-muted">{cfg.nomeConta} · a partir de {curta}</p>
        <div className="space-y-2 rounded border border-border/70 bg-surface2/40 p-4">
          <p className="text-15 font-medium text-white">O que aconteceu</p>
          <Linha rotulo="Lançamentos encontrados">{resumo.importadas}</Linha>
          <Linha rotulo="Aguardando você aceitar (caixa de entrada)"><span className="text-primary">{resumo.pendencias}</span></Linha>
          <Linha rotulo="Já aceitos automaticamente">{resumo.aceitasAutomaticamente}</Linha>
          <Linha rotulo={`Anteriores a ${curta} (não importados)`}>{resumo.historicoIgnorado}</Linha>
          {resumo.saldoAbertura != null && <Linha rotulo={`Saldo inicial em ${curta}`}>{formatMoney(resumo.saldoAbertura)}</Linha>}
        </div>
        {mostraSaldo && (
          <div className="space-y-2 rounded border border-info/50 bg-surface2/40 p-4">
            <p className="text-13 font-semibold text-info">Seu saldo vai fechar com o banco</p>
            <Linha rotulo={`Agora (só os ${resumo.aceitasAutomaticamente} aceitos)`}>{formatMoney(resumo.saldoAgora!)}</Linha>
            <div className="h-2 overflow-hidden rounded-full bg-surface3"><div className="h-2 bg-info" style={{ width: `${pct}%` }} /></div>
            <Linha rotulo="Banco (depois de aceitar tudo)">{formatMoney(resumo.saldoFinal!)}</Linha>
            <p className="text-2xs text-muted">Aceite os lançamentos da caixa de entrada para chegar lá. Os novos lançamentos do banco continuam chegando por lá.</p>
          </div>
        )}
        {resumo.pendencias > 0 && !ehCartao && (
          <p className="rounded border border-warning/50 bg-warning/10 p-3 text-13 text-warning">
            Enquanto houver pendência na caixa de entrada, o app não corrige o saldo sozinho. Resolva a caixa para fechar com o banco.
          </p>
        )}
        {resumo.truncado && (
          <p className="rounded border border-warning/50 bg-warning/10 p-3 text-13 text-warning">
            O banco trouxe mais lançamentos do que a importação inicial consegue buscar de uma vez; o começo do período pode estar incompleto.
          </p>
        )}
        {resumo.avisos.map((a) => <p key={a} className="text-13 text-warning"><i className="ri-information-line" aria-hidden /> {a}</p>)}
      </div>
    </Modal>
  )
}
